/**
 * App — auth gate → router. The shell is a layout route (<AppShell/>) wrapping
 * every in-app page; full-screen flows (onboarding/activate/login) sit outside.
 *
 * Routes are generated from the unified nav config so the whole sidebar is
 * navigable; each id maps to a code-split page chunk (lazyPage) — anything not
 * yet migrated falls back to <Placeholder>. Salary routes are double-gated by
 * <RequireRole group="compensation">.
 */
import { lazy, useMemo, type ComponentType } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AppShell } from './components/AppShell';
import { RequireRole } from './components/RequireRole';
import { Dashboard } from './routes/Dashboard';
import { Placeholder } from './routes/Placeholder';
import { PasswordLogin } from './routes/PasswordLogin';
import { GuidePage } from './routes/GuidePage';
import { SIDEBAR_SECTIONS } from './nav/config';

/** Lazy-load a named route export as its own chunk. */
function lazyPage(loader: () => Promise<Record<string, ComponentType>>, name: string): ComponentType {
  return lazy(() => loader().then((m) => ({ default: m[name] as ComponentType }))) as unknown as ComponentType;
}

const PAGE_COMPONENTS: Record<string, ComponentType> = {
  // people & org
  employees: lazyPage(() => import('./routes/EmployeesPage'), 'EmployeesPage'),
  probation: lazyPage(() => import('./routes/ProbationPage'), 'ProbationPage'),
  career: lazyPage(() => import('./routes/CareerPage'), 'CareerPage'),
  blueprint: lazyPage(() => import('./routes/BlueprintPage'), 'BlueprintPage'),
  'onboarding-journey': lazyPage(() => import('./routes/OnboardingPage'), 'OnboardingPage'),
  org: lazyPage(() => import('./routes/OrgPage'), 'OrgPage'),
  'position-jd': lazyPage(() => import('./routes/PositionJdPage'), 'PositionJdPage'),
  competency: lazyPage(() => import('./routes/CompetencyPage'), 'CompetencyPage'),
  'core-values': lazyPage(() => import('./routes/CoreValuesPage'), 'CoreValuesPage'),
  // SoT (ADR 0003 addendum): setup-wizard + templates created org structure in
  // Eval — removed; PayPilot owns org. Pages deleted.
  // work & goals
  tasks: lazyPage(() => import('./routes/TasksPage'), 'TasksPage'),
  objectives: lazyPage(() => import('./routes/ObjectivesPage'), 'ObjectivesPage'),
  'kpi-metrics': lazyPage(() => import('./routes/KpiMetricsPage'), 'KpiMetricsPage'),
  'task-templates': lazyPage(() => import('./routes/TaskTemplatesPage'), 'TaskTemplatesPage'),
  'workhub-catalog': lazyPage(() => import('./routes/WorkhubCatalogPage'), 'WorkhubCatalogPage'),
  ranking: lazyPage(() => import('./routes/RankingPage'), 'RankingPage'),
  'paired-comparison': lazyPage(() => import('./routes/PairedComparisonPage'), 'PairedComparisonPage'),
  'cycle-review': lazyPage(() => import('./routes/CycleReviewPage'), 'CycleReviewPage'),
  // data capture
  observations: lazyPage(() => import('./routes/ObservationsPage'), 'ObservationsPage'),
  incidents: lazyPage(() => import('./routes/IncidentsPage'), 'IncidentsPage'),
  checkins: lazyPage(() => import('./routes/CheckinsPage'), 'CheckinsPage'),
  'quality-logs': lazyPage(() => import('./routes/QualityLogsPage'), 'QualityLogsPage'),
  psychometric: lazyPage(() => import('./routes/PsychometricPage'), 'PsychometricPage'),
  // evaluation
  evaluation: lazyPage(() => import('./routes/EvaluationPage'), 'EvaluationPage'),
  'assessment-packages': lazyPage(() => import('./routes/AssessmentPackagesPage'), 'AssessmentPackagesPage'),
  // engagement (รักษาคน)
  pulse: lazyPage(() => import('./routes/PulsePage'), 'PulsePage'),
  recognition: lazyPage(() => import('./routes/RecognitionPage'), 'RecognitionPage'),
  // my development
  'dev-plans': lazyPage(() => import('./routes/DevPlansPage'), 'DevPlansPage'),
  'dev-plan-builder': lazyPage(() => import('./routes/DevPlanBuilderPage'), 'DevPlanBuilderPage'),
  certifications: lazyPage(() => import('./routes/CertificationsPage'), 'CertificationsPage'),
  'learning-calendar': lazyPage(() => import('./routes/LearningCalendarPage'), 'LearningCalendarPage'),
  'mentor-sharing': lazyPage(() => import('./routes/MentorSharingPage'), 'MentorSharingPage'),
  // L&D
  'lnd-overview': lazyPage(() => import('./routes/LndOverviewPage'), 'LndOverviewPage'),
  'lnd-plans': lazyPage(() => import('./routes/LndPlansPage'), 'LndPlansPage'),
  'lnd-vendors-courses': lazyPage(() => import('./routes/LndVendorsCoursesPage'), 'LndVendorsCoursesPage'),
  'lnd-reports': lazyPage(() => import('./routes/LndReportsPage'), 'LndReportsPage'),
  'lnd-skill-group': lazyPage(() => import('./routes/LndSkillGroupPage'), 'LndSkillGroupPage'),
  'lnd-settings': lazyPage(() => import('./routes/LndSettingsPage'), 'LndSettingsPage'),
  'tenant-policies': lazyPage(() => import('./routes/TenantPoliciesPage'), 'TenantPoliciesPage'),
  // docs & knowledge
  'document-generator': lazyPage(() => import('./routes/DocumentGeneratorPage'), 'DocumentGeneratorPage'),
  'documents-list': lazyPage(() => import('./routes/DocumentsListPage'), 'DocumentsListPage'),
  documents: lazyPage(() => import('./routes/DocumentsPage'), 'DocumentsPage'),
  library: lazyPage(() => import('./routes/LibraryPage'), 'LibraryPage'),
  'assessment-knowledge': lazyPage(() => import('./routes/AssessmentKnowledgePage'), 'AssessmentKnowledgePage'),
  // delegation & approval (owner-only via NAV_BY_ROLE group 'delegation')
  'pending-inbox': lazyPage(() => import('./routes/PendingInboxPage'), 'PendingInboxPage'),
  // settings & admin
  settings: lazyPage(() => import('./routes/SettingsPage'), 'SettingsPage'),
  users: lazyPage(() => import('./routes/UsersPage'), 'UsersPage'),
  admin: lazyPage(() => import('./routes/AdminPage'), 'AdminPage'),
  // overview
  calendar: lazyPage(() => import('./routes/CalendarPage'), 'CalendarPage'),
  'system-overview': lazyPage(() => import('./routes/SystemOverviewPage'), 'SystemOverviewPage'),
  'workflow-guide': lazyPage(() => import('./routes/WorkflowGuidePage'), 'WorkflowGuidePage'),
};

// capture-catalog (toggle catalog) — a sub-tab, not a sidebar item.
const CaptureCatalogPage = lazyPage(() => import('./routes/CaptureCatalogPage'), 'CaptureCatalogPage');
// Notification inbox — reached via the header bell, not a sidebar item. Every
// role sees their own notifications, so no role gate.
const NotificationsPage = lazyPage(() => import('./routes/NotificationsPage'), 'NotificationsPage');
const DevPlanDetailPage = lazyPage(() => import('./routes/DevPlanDetailPage'), 'DevPlanDetailPage');

// Group "guide" sub-tabs (reached via SubNav, not the sidebar) → one config-driven
// <GuidePage>, gated by their group so e.g. STAFF can't open the compensation guide.
const GUIDE_ROUTES: { id: string; g: string }[] = [
  { id: 'org-guide', g: 'org' },
  { id: 'people-guide', g: 'people' },
  { id: 'evaluation-guide', g: 'evaluation' },
  { id: 'work-hub-guide', g: 'work_hub' },
  { id: 'my-dev-guide', g: 'my_dev' },
  { id: 'lnd-guide', g: 'l_and_d' },
  { id: 'data-capture-guide', g: 'data_capture' },
];

export function App() {
  const { status, error, refreshAuth } = useAuth();

  const navItems = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; g: string }[] = [];
    for (const s of SIDEBAR_SECTIONS) {
      for (const it of s.items) {
        if (seen.has(it.id) || it.id === 'dashboard') continue;
        seen.add(it.id);
        out.push({ id: it.id, g: it.g });
      }
    }
    return out;
  }, []);

  if (status === 'loading') return <FullScreen>กำลังโหลด…</FullScreen>;

  if (status === 'error') {
    return (
      <FullScreen>
        <p className="text-red-600">เกิดข้อผิดพลาด: {error}</p>
        <button className="btn-primary mt-3" onClick={() => void refreshAuth(true)}>
          ลองใหม่
        </button>
      </FullScreen>
    );
  }

  if (status === 'no-liff') return <PasswordLogin />;

  if (status === 'unpaired') {
    return (
      <FullScreen>
        <p className="font-medium">ยังไม่ได้ผูกบัญชีกับองค์กร</p>
        <p className="text-sm text-gray-500 mt-1">กรุณาลงทะเบียนผ่านลิงก์เชิญจากองค์กรของคุณ</p>
      </FullScreen>
    );
  }

  if (status === 'needs-activation') {
    return (
      <FullScreen>
        <p className="font-medium">ยังไม่ได้เปิดใช้งาน Eval Pilot</p>
        <p className="text-sm text-gray-500 mt-1">องค์กรของคุณยังไม่ได้เปิดแพ็กเกจ — ติดต่อผู้ดูแลระบบเพื่อเปิดใช้งาน</p>
      </FullScreen>
    );
  }

  return (
    <Routes>
      {/* Full-screen flows — outside the shell */}
      <Route path="/onboarding" element={<FullScreen>ลงทะเบียนองค์กร (placeholder)</FullScreen>} />
      <Route path="/activate" element={<FullScreen>เปิดใช้งานแพ็กเกจ (placeholder)</FullScreen>} />

      {/* Shell layout */}
      <Route element={<AppShell />}>
        <Route index element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/launcher" element={<Placeholder />} />
        {navItems.map((it) => {
          const Cmp = PAGE_COMPONENTS[it.id] ?? Placeholder;
          return (
            <Route
              key={it.id}
              path={`/${it.id}`}
              element={
                <RequireRole group={it.g}>
                  <Cmp />
                </RequireRole>
              }
            />
          );
        })}
        {GUIDE_ROUTES.map((it) => (
          <Route
            key={it.id}
            path={`/${it.id}`}
            element={
              <RequireRole group={it.g}>
                <GuidePage />
              </RequireRole>
            }
          />
        ))}
        <Route
          path="/capture-catalog"
          element={
            <RequireRole group="data_capture">
              <CaptureCatalogPage />
            </RequireRole>
          }
        />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* Dev plan detail + closed-loop progress (param route, not a nav item). */}
        <Route
          path="/dev-plan-detail/:id"
          element={
            <RequireRole group="my_dev">
              <DevPlanDetailPage />
            </RequireRole>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-6 text-center text-gray-700">{children}</main>
  );
}
